/**
 * Scenes bar logic (F4 "Scenes bar", frame 12a) — the pure half of `components/watch/ScenesBar`.
 * Timeline maths, span clamping (mirrors the server's clamp so a 422 is never the first thing a
 * learner sees), auto-naming ("Scene at m:ss").
 */
import type { Scene } from '@nelnela/contracts';
import { fmtTime } from './text';

export interface SceneSpan {
  startMs: number;
  endMs: number;
}

/**
 * Clamp a pending in/out pair to the video: start < 0 → 0; end past the duration → duration;
 * start at/after the duration or end ≤ start (after clamping) → null (nothing to create).
 * `durationMs` null/0 = unknown → only the ordering rules apply.
 */
export function clampSceneSpan(
  startMs: number,
  endMs: number,
  durationMs: number | null,
): SceneSpan | null {
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return null;
  let start = Math.max(0, Math.round(startMs));
  let end = Math.round(endMs);
  if (durationMs != null && durationMs > 0) {
    if (start >= durationMs) return null;
    if (end > durationMs) end = durationMs;
  }
  if (end <= start) return null;
  return { startMs: start, endMs: end };
}

/** Auto-name for a new scene at `startMs` (04-flows F4: `Scene at 12:34`). */
export function autoSceneName(startMs: number): string {
  return `Scene at ${fmtTime(startMs)}`;
}

/** Position of `ms` on the timeline as a percentage 0..100 (0 when the duration is unknown). */
export function pct(ms: number, durationMs: number | null): number {
  if (durationMs == null || !(durationMs > 0) || !Number.isFinite(ms)) return 0;
  return Math.min(100, Math.max(0, (ms / durationMs) * 100));
}

/** Inverse of `pct` for a timeline tap: fraction 0..1 of the width → ms (null when unknown). */
export function msAtFraction(fraction: number, durationMs: number | null): number | null {
  if (durationMs == null || !(durationMs > 0) || !Number.isFinite(fraction)) return null;
  return Math.round(Math.min(1, Math.max(0, fraction)) * durationMs);
}

/** Stable display order: by startMs, then id (the server returns creation order). */
export function sortScenes(scenes: readonly Scene[]): Scene[] {
  return [...scenes].sort(
    (a, b) => a.startMs - b.startMs || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
  );
}

/** The scene whose span contains `ms` (start inclusive, end exclusive) — first in `sortScenes` order; null if none. */
export function sceneAt(scenes: readonly Scene[], ms: number): Scene | null {
  for (const s of sortScenes(scenes)) if (ms >= s.startMs && ms < s.endMs) return s;
  return null;
}

/** `12:30 – 13:45` — the range label on a scene chip (frame 12a). */
export function sceneRange(scene: Pick<Scene, 'startMs' | 'endMs'>): string {
  return `${fmtTime(scene.startMs)} – ${fmtTime(scene.endMs)}`;
}
